import groupBy from 'lodash/groupBy'
import type { OperationResult } from 'urql'

import type { BoundedState } from '@app/store/types'
import type { DomainObject } from '@app/types'
import Normalizer from '@graphql/normalizer'
import type { CrudOperationEnum } from '@graphql/types'

export type StoreOperations = Pick<BoundedState, 'addObject' | 'deleteObject' | 'bulkAdd' | 'bulkDelete'>

const normalizer = new Normalizer({
  skipTypenames: [
    'Attachment',
    'CalculatedMetricInput',
    'Classification',
    'CustomerSubscription',
    'EntityAttachment',
    'EntityContainment',
    'EntityContribution',
    'FileAttachment',
    'Label',
    'MetricSourceActivity',
    'Organization',
    'SourceIdentity',
    'StrategyStat',
    'SegmentName',
    'SegmentValue',
    'SegmentFilter'
  ]
})

const crudOperation = (result: OperationResult): CrudOperationEnum => {
  // We allow both the operation result itself, or an immediate child subfield to act as the operation.
  // This allows us to both send the result directly for subscriptions, as well as add additional fields in mutations.
  // Note that `crudOperation` naming is NOT optional. Map fields in your queries as needed.
  const baseCrudOp = result?.data?.crudOperation
  return baseCrudOp?.operation || baseCrudOp?.crudOperation?.operation
}

const handleGraphqlResult = (result: OperationResult, storeOperations: StoreOperations) => {
  const bulkAdd = storeOperations?.bulkAdd
  const bulkDelete = storeOperations?.bulkDelete

  if (!bulkAdd || !bulkDelete) {
    return
  }

  try {
    const normalized: Partial<Record<keyof BoundedState, Record<string, DomainObject>>> = normalizer.normalize(result)

    const crudOp = crudOperation(result)
    const obs = []

    Object.values(normalized).forEach((objects) => {
      Object.values(objects).forEach((object) => {
        if (object.classType) {
          obs.push(object)
        }
      })
    })

    try {
      if (crudOp === 'delete') {
        const entries = Object.entries(groupBy(obs, 'classType'))
        const deleteObs = entries.map(([classType, objects]) => ({
          [classType]: objects.map((object) => object.id)
        }))

        bulkDelete(deleteObs, { skipMutation: true })
      } else {
        bulkAdd(obs)
      }
    } catch (_e) {
      // console.error('Error handling individual graphql object', e, object)
    }
  } catch (_e) {
    // console.error('normalizer error', e)
  }
}

export default handleGraphqlResult
